/* eslint-disable max-lines */
import styled, { css } from "styled-components";
import { Flex } from "grid-styled";
import { Blank, FlatButton, Icon, IconButton, Message, SwitchTitle, Textarea, transition } from "@evergis/ui";

import { ConditionOperationTitleProps } from "./types";

const titleMixin = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  color: ${({ theme }) => theme.palette.textPrimary};
`;

export const MainContainer = styled(Flex)`
  flex-shrink: 0;
  flex-direction: column;
`;

export const Content = styled(Flex)`
  flex-direction: column;
  flex-grow: 1;
`;

export const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 48px;
  background-color: ${({ theme: { palette } }) => palette.elementDark};
  padding: 0 1rem 0 1.5rem;
  box-sizing: border-box;
`;

export const ActionsSwitcher = styled.div`
  display: flex;
  align-items: center;
`;

export const ActionsContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
`;

export const ActionsDivider = styled.div`
  width: 1px;
  height: 1rem;
  background-color: rgba(0, 0, 0, 0.12);
  margin: 0 0.5rem;
`;

export const LayerFilterContainer = styled(Flex)`
  flex-direction: column;
  height: 100%;
  flex-grow: 1;
  flex-wrap: nowrap;
  padding-top: 0.5rem;
  box-sizing: border-box;
  overflow: auto;
`;

export const Header = styled(Flex)`
  flex-shrink: 0;
  box-sizing: border-box;
  width: 100%;
  height: 3.375rem;
  padding: 0 1.5rem;
  align-items: center;
  justify-content: space-between;
`;

export const Title = styled.div`
  ${titleMixin};
  font-style: normal;
  font-weight: 400;
  font-size: 24px;
  line-height: 28px;
  letter-spacing: -0.12px;
  padding: 1.5rem;
  box-sizing: border-box;
`;

export const SubTitle = styled.div`
  ${titleMixin};
  font-style: normal;
  font-weight: 400;
  font-size: 20px;
  line-height: 23px;
`;

export const ConditionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 0 1rem;
  flex-shrink: 0;
`;

export const FilterConditionsWrapper = styled.div`
  width: 100%;
  overflow-y: auto;
`;

export const FilterConditionsContainer = styled.div`
  width: 100%;
  flex-grow: 0;
  flex-direction: column;
  overflow-y: auto;
  display: flex;
  padding: 0 0.75rem;
  box-sizing: border-box;
`;

export const AddConditionButtonContainer = styled(Flex)`
  align-items: center;
  padding: 0 1.5rem;
  margin-bottom: 1rem;
  box-sizing: border-box;
`;

export const ClearConditionsButtonContainer = styled(Flex)`
  & button {
    & > span:nth-child(1) {
      order: 2;
    }

    & > span:nth-child(2) {
      order: 1;
      margin: 0 0.5rem 0 0;
    }
  }
`;

export const ComplexConditionsContainer = styled.div`
  justify-content: center;
  display: flex;
  align-items: center;
  flex-direction: column;
  height: 100%;
  margin-top: 0.5rem;
  padding: 0.5rem 1.5rem;
  box-sizing: border-box;

  ${Message} {
    font-weight: 400;
    font-size: 12px;
    line-height: 14px;
    letter-spacing: 0.1px;
    white-space: pre-wrap;
    margin-bottom: 1rem;
    width: 100%;
  }

  ${FlatButton} {
    max-width: none;
  }
`;

export const DevModeButtonContainer = styled(Flex)`
  align-items: center;
`;

export const AttributeBlank = styled(Blank)`
  display: flex;
  padding: 0.75rem 1.5rem 0.75rem 1.5rem;
  box-shadow: none;
  align-items: center;

  > div:not(:last-of-type) {
    margin-right: 0.875rem;
  }
`;

export const ConditionBlank = styled(AttributeBlank)`
  width: 100%;
  padding: 0.5rem 0 0.5rem 0.5rem;
  box-sizing: border-box;
`;

export const ConditionBlankInner = styled(Flex)`
  justify-content: space-between;
  width: 100%;
`;

export const ConditionDeleteButton = styled(IconButton)`
  margin-top: 1.35rem;
`;

export const ConditionAttributeContainer = styled.div`
  flex-grow: 1;
  width: calc(50% - 1rem);
`;

export const FilterInputContainer = styled.div`
  position: relative;
  flex-grow: 1;
  width: calc(50% - 1rem);
  margin-left: -1px;
`;

export const FilterInputParamSwitchContainer = styled.div`
  position: absolute;
  right: 0;
  top: 0;

  & > button {
    height: 1.65rem;

    & > span::after {
      font-size: 12px;
    }
  }
`;

export const ConditionEditorContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  padding: 0 1.5rem 0.75rem 1.5rem;
  box-sizing: border-box;
`;

export const ConditionEditorInput = styled(Textarea)`
  width: 100%;
  height: ${({ error }) => (error ? "calc(100% - 3.5rem)" : "calc(100% - 1.5rem)")};
  margin-top: 0.75rem;
`;

export const ConditionOperationTitle = styled(SwitchTitle)<ConditionOperationTitleProps>`
  && {
    cursor: pointer;
    color: ${({ theme: { palette }, checked }) => (checked ? palette.primary : palette.textPrimary)} !important;
  }
`;

export const AndConditionTitle = styled(ConditionOperationTitle)<ConditionOperationTitleProps>`
  margin: 0 0.5rem 0 0;
`;

export const OrConditionTitle = styled(ConditionOperationTitle)``;

export const EqlParamsWrapper = styled.div`
  margin: 1.5rem 1.5rem 0;
  box-sizing: border-box;
`;

export const EqlParamsContainer = styled(Flex)`
  flex-direction: column;
  padding: 1rem;
  background-color: ${({ theme: { palette } }) => palette.element};
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.xSmall};
`;

export const EqlParamsTitle = styled.span`
  ${titleMixin};
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 0.00625rem;
`;

export const EqlParamsShowButton = styled(Flex)<{ $isOpen?: boolean }>`
  cursor: pointer;
  align-items: center;
  font-size: 0.625rem;
  color: ${({ theme: { palette } }) => palette.textSecondary};
  transition: color ${transition.hover};

  span[kind="expand"] {
    transform: ${({ $isOpen }) => `rotate(${$isOpen ? 180 : 0}deg)`};
    transition: color ${transition.hover}, transform ${transition.hover} cubic-bezier(0.15, 1.35, 0.7, 1.5);
    transition: transform 300ms cubic-bezier(0.15, 1.35, 0.7, 1.5), opacity ${transition.hover};
  }

  :hover {
    color: ${({ theme: { palette } }) => palette.textPrimary};

    span[kind="expand"] {
      color: ${({ theme: { palette } }) => palette.textPrimary};
    }
  }
`;

export const EqlParamsList = styled(Flex)`
  flex-direction: column;
  margin: 1rem 0 0.5rem;
  gap: 1rem;
`;

export const EqlParamsInfo = styled(Icon)`
  opacity: 0.54;
  transition: opacity ${transition.hover};

  &:hover {
    opacity: 1;
  }
`;

export const EqlTooltipContent = styled.div`
  text-align: left;
`;
